import axios from 'axios';
import request from '@/utils/request'; // 确保导入了request

// 创建axios实例
const apiClient = axios.create({
  // 在开发环境中使用代理，生产环境中使用实际的后端地址
  baseURL: '/api', // 使用代理
  timeout: 30000000, // 请求超时时间增加到30秒
});

// 请求拦截器 - 在每个请求头中添加token
apiClient.interceptors.request.use(
  config => {
    // 从localStorage获取token
    const token = localStorage.getItem('token');
    if (token) {
      // 修改为token头部，与后端保持一致
      config.headers!['token'] = token;
      console.log('✓ Token added to request:', token.substring(0, 20) + '...');
    } else {
      console.warn('No token found in localStorage');
    }
    
    // 设置正确的Content-Type，但仅在非multipart/form-data时设置
    if (!config.headers!['Content-Type'] && !config.headers!['content-type']) {
      // 检查是否是multipart/form-data请求（文件上传）
      if (config.data instanceof FormData) {
        // 对于FormData，让浏览器自动设置Content-Type，包括boundary
        delete config.headers!['Content-Type'];
      } else {
        config.headers!['Content-Type'] = 'application/json';
      }
    }
    
    console.log('Request config headers:', config.headers);
    return config;
  },
  error => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理响应数据
apiClient.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    // 处理错误状态码
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // 清除token并跳转到登录页
          localStorage.removeItem('token');
          window.location.href = '/login';
          break;
        case 403:
          // 权限不足提示
          console.error('权限不足');
          break;
      }
    }
    return Promise.reject(error);
  }
);

/**
 * 帖子相关类型定义
 */

export interface PostItem {
  id: number;
  userId: number;
  name: string;
  userAvatar: string;
  title: string;
  content: string;
  coverImage: string;
  status: number;
  createTime: string;
  viewCount: number;
  likeCount: number;
  favoriteCount: number;
  commentCount: number;
  isLiked: number;
  isFavorited: number;
  isFollowed: number;
  isFollowerd: number; // 新增字段：帖子作者是否关注当前登录用户 0否1是
  hotComment: Comment | null;
  mediaList?: MediaItem[]; // 媒体列表，可选
}

export interface PostDetail {
  id: number;
  userId: number;
  name: string;
  userAvatar: string;
  title: string;
  content: string;
  coverImage: string;
  status: number;
  createTime: string;
  viewCount: number;
  likeCount: number;
  favoriteCount: number;
  commentCount: number;
  isLiked: number;
  isFavorited: number;
  isFollowed: number;
  isFollowerd: number; // 新增字段：帖子作者是否关注当前登录用户 0否1是
  hotComment: Comment | null;
  comments: Comment[];
  mediaList: MediaItem[];
}

export interface PostListResponse {
  code: number;
  msg: string | null;
  data: {
    records: PostItem[];
    total: number;
  };
}

export interface PostDetailResponse {
  code: number;
  msg: string | null;
  data: PostDetail;
}

/**
 * 获取帖子列表接口
 * @param params - 请求参数
 */
export function postList(params: { 
  page: number, 
  pageSize: number, 
  keyword?: string 
}): Promise<PostListResponse> {
  return apiClient.post('/user/post/list', params, {
    headers: {
      'Content-Type': 'application/json'
    }
  }).then(response => response.data);
}

/**
 * 获取帖子详情接口
 * @param postId - 帖子ID
 */
export const getPostDetail = async (postId: number): Promise<PostDetailResponse> => {
  const response = await apiClient.get(`/user/post/${postId}`);
  return response.data;
};

/**
 * 定义评论相关的类型
 */
export interface Comment {
  id: number;
  postId: number;
  userId: number;
  name: string;
  userAvatar: string;
  parentId: number | null;
  content: string;
  likeCount: number;
  createTime: string;
  isLiked: boolean | null | 0 | 1;  // 点赞状态，true或1为已点赞，false或0为未点赞，null为未确定
  replyCount?: number; // 新增：评论的回复数量
  childCommentCount?: number; // 新增：当前评论的子评论数
}

export interface CommentListParams {
  page: number;
  pageSize: number;
  postId: number;
}

// 后端直接返回 {total: number, records: Comment[]} 结构
export interface CommentListResponse {
  code: number;
  msg: string | null;
  data: {
    total: number;
    records: Comment[];
  };
}

// 子评论的请求参数接口
export interface ChildCommentListParams {
  page: number;
  pageSize: number;
  postId: number;
  parentId: number; // 父评论ID
}

// 子评论的响应接口
export interface ChildCommentListResponse {
  code: number;
  msg: string | null;
  data: {
    total: number;
    records: Comment[];
  };
}

// 获取帖子下的评论列表
export const getCommentList = (params: CommentListParams): Promise<CommentListResponse> => {
  return apiClient.post('/user/comment/list', params, {
    headers: {
      'Content-Type': 'application/json'
    }
  }).then(response => {
    // 直接返回后端的响应数据，因为后端直接返回 {total: 5, records: [...]} 结构
    // 我们将其包装成 CommentListResponse 结构
    return {
      code: 1, // 假设请求成功
      msg: null,
      data: response.data // response.data 包含 {total: 5, records: [...]}
    };
  });
};

// 获取子评论列表
export const getChildCommentList = (params: ChildCommentListParams): Promise<ChildCommentListResponse> => {
  console.log('调用getChildCommentList API，参数:', params);
  
  return apiClient.post('/user/comment/childlist', params, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
  .then(response => {
    console.log('子评论API响应:', response);
    // 返回符合ChildCommentListResponse接口的数据结构
    return {
      code: 1,
      msg: null,
      data: response.data // response.data 包含 {total: number, records: [...]}
    };
  })
  .catch(error => {
    console.error('子评论API请求失败:', error);
    throw error; // 重新抛出错误，让调用方处理
  });
};

export default apiClient;

/**
 * 创建评论接口
 * @param params - 评论参数
 */
export interface CreateCommentParams {
  postId: number;      // 帖子ID
  parentId?: number;   // 父评论ID，可选
  content: string;     // 评论内容
}

export interface CreateCommentResponse {
  code: number;
  msg: string;
  data: any;           // 根据实际返回的数据结构调整
}

export function createComment(params: CreateCommentParams): Promise<CreateCommentResponse> {
  return apiClient.post('/user/comment/create', params, {
    headers: {
      'Content-Type': 'application/json'
    }
  }).then(response => response.data);
}

/**
 * 点赞/取消点赞帖子
 * @param postId - 帖子ID
 */
export function likePost(postId: number) {
  return apiClient.put(`/user/interaction/likePost/${postId}`).then(response => response.data);
}

/**
 * 收藏/取消收藏帖子
 * @param postId - 帖子ID
 */
export function favoritePost(postId: number) {
  return apiClient.put(`/user/interaction/favorite/${postId}`).then(response => response.data);
}

/**
 * 关注/取消关注用户
 * @param followUserId - 被关注的用户ID
 */
export function followUser(followUserId: number) {
  return apiClient.put(`/user/interaction/follow/${followUserId}`).then(response => response.data);
}

/**
 * 创建帖子
 * @param params - 帖子参数
 */
export interface CreatePostParams {
  title: string;
  content: string;
  coverImage?: string; // 封面图片路径，可选
}

export interface CreatePostResponse {
  code: number;
  msg: string | null;
  data: any; // 根据实际返回的数据结构调整
}

export function createPost(params: CreatePostParams): Promise<CreatePostResponse> {
  return apiClient.post('/user/post/create', params, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
  .then(response => {
    // 确保响应和数据存在
    if (!response) {
      console.error('API响应为空');
      throw new Error('API响应为空');
    }
    
    if (!response.data) {
      console.error('API响应中data字段为空', response);
      throw new Error('API响应中data字段为空');
    }
    
    // 返回响应数据
    return response.data;
  })
  .catch(error => {
    console.error('创建帖子API调用失败:', error);
    throw error; // 重新抛出错误，让调用方处理
  });
}

/**
 * 删除帖子
 * @param postId - 帖子ID
 */
export function deletePost(postId: number) {
  return apiClient.delete(`/user/post/${postId}`)
    .then(response => response.data);
}

/**
 * 编辑帖子
 * @param params - 帖子参数
 */
export interface EditPostParams {
  id: number;
  title: string;
  content: string;
  coverImage?: string; // 封面图片路径，可选
  mediaList?: MediaItem[]; // 媒体列表，可选
}

export interface EditPostResponse {
  code: number;
  msg: string | null;
  data: any; // 根据实际返回的数据结构调整
}

export function editPost(params: EditPostParams): Promise<EditPostResponse> {
  return apiClient.post('/user/post/edit', params, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
  .then(response => {
    // 确保响应和数据存在
    if (!response) {
      console.error('API响应为空');
      throw new Error('API响应为空');
    }
    
    if (!response.data) {
      console.error('API响应中data字段为空', response);
      throw new Error('API响应中data字段为空');
    }
    
    // 返回响应数据
    return response.data;
  })
  .catch(error => {
    console.error('编辑帖子API调用失败:', error);
    throw error; // 重新抛出错误，让调用方处理
  });
}

/**
 * 删除评论
 * @param commentId - 评论ID
 */
export function deleteComment(commentId: number) {
  return apiClient.delete(`/user/comment/${commentId}`)
    .then(response => response.data);
}

/**
 * 批量上传图片
 * @param formData - 图片文件表单数据
 * @param onUploadProgress - 上传进度回调函数
 */
export interface UploadImagesResponse {
  code: number;
  msg: string | null;
  data: string[]; // 上传成功后返回的图片路径列表
}

export function uploadImages(formData: FormData, onUploadProgress?: (progressEvent: any) => void): Promise<UploadImagesResponse> {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress // 添加进度回调
  };

  return apiClient.post('/user/post/uploadImages', formData, config)
    .then(response => response.data);
}

/**
 * 上传视频
 * @param file - 视频文件
 * @param onUploadProgress - 上传进度回调函数
 */
export interface UploadVideoResponse {
  code: number;
  msg: string | null;
  data: string; // 上传成功后返回的视频路径
}

export function uploadVideo(file: File, onUploadProgress?: (progressEvent: any) => void): Promise<UploadVideoResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress // 添加进度回调
  };

  return apiClient.post('/user/post/uploadVideo', formData, config)
    .then(response => response.data);
}

/**
 * 媒体项类型定义
 */
export interface MediaItem {
  id: number;
  mediaType: number; // 1-图片，2-视频
  mediaUrl: string;
  width: number;
  height: number;
  sortOrder: number | null;
  thumbnailUrl: string | null;
  duration: number | null;
}

/**
 * 创建帖子参数（包含媒体列表）
 */
export interface CreatePostWithMediaParams {
  title: string;
  content: string;
  coverImage: string;
  mediaList: MediaItem[];
}

/**
 * 创建帖子的响应类型定义
 */
export interface CreatePostResponse {
  code: number;
  msg: string | null;
  data: any; // 根据实际返回的数据结构调整
}

/**
 * 创建带有多媒体的帖子
 * @param params - 帖子参数（包含媒体列表）
 */
export function createPostWithMedia(params: CreatePostWithMediaParams): Promise<CreatePostResponse> {
  return apiClient.post('/user/post/create', params, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
  .then(response => {
    // 确保响应和数据存在
    if (!response) {
      console.error('API响应为空');
      throw new Error('API响应为空');
    }
    
    if (!response.data) {
      console.error('API响应中data字段为空', response);
      throw new Error('API响应中data字段为空');
    }
    
    // 返回响应数据
    return response.data;
  })
  .catch(error => {
    console.error('创建帖子API调用失败:', error);
    throw error; // 重新抛出错误，让调用方处理
  });
}

/**
 * 使用浏览器API获取媒体信息
 */
export async function getMediaInfo(mediaUrl: string): Promise<{ width?: number, height?: number, duration?: number }> {
  try {
    // 在开发环境中，使用代理路径来获取媒体信息
    let processedUrl = mediaUrl;
    if (process.env.NODE_ENV === 'development') {
      // 如果是 /api 开头的路径，浏览器会自动通过代理访问
      if (mediaUrl.startsWith('/api/uploads')) {
        processedUrl = mediaUrl;
      } 
      // 如果是 /uploads 开头的路径，添加 /api 前缀
      else if (mediaUrl.startsWith('/uploads')) {
        processedUrl = `/api${mediaUrl}`;
      } 
      // 如果是完整的后端URL，替换为代理路径
      else if (mediaUrl.startsWith('http://localhost:8080')) {
        processedUrl = mediaUrl.replace('http://localhost:8080', '/api');
      } 
      else {
        // 其他情况保持原样
        processedUrl = mediaUrl;
      }
    }
    
    const isVideo = /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)$/i.test(processedUrl);
    
    if (isVideo) {
      return new Promise((resolve) => {
        const video = document.createElement('video');
        
        video.onloadedmetadata = () => {
          console.log('成功获取视频元数据:', {
            width: video.videoWidth,
            height: video.videoHeight,
            duration: video.duration
          });
          
          resolve({
            width: video.videoWidth,
            height: video.videoHeight,
            duration: video.duration
          });
        };
        
        video.onerror = (e) => {
          console.warn('无法获取视频元数据，返回默认值', e);
          console.warn('尝试访问的URL:', processedUrl);
          resolve({});
        };
        
        video.crossOrigin = 'anonymous'; // 设置跨域
        video.src = processedUrl;
        
        // 加载视频以触发元数据获取
        video.load();
      });
    } else {
      // 创建一个隐藏的img元素来获取图片信息
      return new Promise((resolve) => {
        const img = new Image();
        
        img.onload = () => {
          console.log('成功获取图片尺寸:', {
            width: img.width,
            height: img.height
          });
          
          resolve({
            width: img.width,
            height: img.height
          });
        };
        
        img.onerror = (e) => {
          console.warn('无法获取图片尺寸，返回默认值', e);
          console.warn('尝试访问的URL:', processedUrl);
          resolve({});
        };
        
        img.crossOrigin = 'anonymous'; // 设置跨域
        // 使用处理后的URL
        img.src = processedUrl;
      });
    }
  } catch (error) {
    console.error('获取媒体信息时发生错误:', error);
    return {};
  }
}

/**
 * 创建帖子（使用正确的API路径）
 * @param params - 帖子参数（包含媒体列表）
 */
export function createPostNew(params: CreatePostWithMediaParams): Promise<CreatePostResponse> {
  return apiClient.post('/user/post/create', params, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
  .then(response => {
    // 确保响应和数据存在
    if (!response) {
      console.error('API响应为空');
      throw new Error('API响应为空');
    }
    
    if (!response.data) {
      console.error('API响应中data字段为空', response);
      throw new Error('API响应中data字段为空');
    }
    
    // 返回响应数据
    return response.data;
  })
  .catch(error => {
    console.error('创建帖子API调用失败:', error);
    throw error; // 重新抛出错误，让调用方处理
  });
}

/**
 * 上传封面图片
 * @param coverImage - 封面图片文件
 */
export async function uploadCoverImage(coverImage: File): Promise<any> {
  // 创建FormData对象用于上传文件
  const formData = new FormData();
  formData.append('coverImage', coverImage); // 根据接口要求使用coverImage作为字段名

  // 发送POST请求上传封面图片
  return apiClient.post('/user/post/addCoverImage', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
  .then(response => {
    // 确保响应和数据存在
    if (!response) {
      console.error('上传封面图片API响应为空');
      throw new Error('上传封面图片API响应为空');
    }

    if (!response.data) {
      console.error('上传封面图片API响应中data字段为空', response);
      throw new Error('上传封面图片API响应中data字段为空');
    }

    // 返回响应数据
    return response.data;
  })
  .catch(error => {
    console.error('上传封面图片API调用失败:', error);
    throw error; // 重新抛出错误，让调用方处理
  });
}

/**
 * AI润色帖子内容（流式输出）
 */
export const polishContent = (content: string, instruction?: string) => {
  return request({
    url: '/user/post/polish',
    method: 'POST',
    data: {
      content,
      instruction: instruction || '让语言更生动有趣'
    },
    responseType: 'stream'
  });
};
